import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Repeat2,
  Route,
  Settings2,
  ShieldAlert,
  Voicemail,
  SlidersHorizontal,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { AmdSettingsModal } from './AmdSettings';
import { DialAdmissionModal, stoppedDot, stoppedLabel } from './DialAdmission';
import { EventTransportModal, countQueued } from './EventTransportSwitch';
import { GatekeeperSettingsModal } from './GatekeeperSettings';
import { InboundCallModeModal, useInboundStatus } from './InboundCallMode';
import { ManualDialerModal, useManualDialerStatus } from './ManualDialer';
import { NumbersDashboard } from './NumbersDashboard';
import { RedialLadderModal } from './RedialLadder';
import { EVENT_STREAMS, fetchDialerSettings } from './api';

type Panel =
  | 'inbound'
  | 'manual'
  | 'admission'
  | 'redial'
  | 'amd'
  | 'gatekeeper'
  | 'transport'
  | 'numbers';

// A row's live state, rendered identically for every row that has one so the menu reads as
// one column rather than four ad-hoc suffixes.
function RowStatus({ dot, label }: { dot?: string; label: string }) {
  return (
    <span className="text-muted-foreground ml-auto flex items-center gap-1.5 pl-4 text-xs">
      {dot && <span aria-hidden className={cn('inline-block h-2 w-2 rounded-full', dot)} />}
      {label}
    </span>
  );
}

// Every global control the console owns, behind one trigger, so they are not mistaken for
// the page links or the board's own actions. The separator splits them by how often they
// are touched: above it the live switches an operator reaches for during an incident,
// below it tuning that is set and left. What a closed menu would otherwise hide comes back
// out on the trigger as a dot.
export function ConsoleSettingsMenu() {
  const [panel, setPanel] = useState<Panel | null>(null);
  const [queued, setQueued] = useState<number | null>(null);
  const [stopped, setStopped] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  // Monotonic epoch over every source of the poll's values: each read takes a number at
  // SEND time and only the latest sender may apply its response, so a slow response cannot
  // overwrite the fresher truth a modal load/save just wrote. One epoch covers both values,
  // so a transport save also drops that tick's stopped count — stale for one interval.
  const epochRef = useRef(0);

  const inbound = useInboundStatus();
  const manual = useManualDialerStatus();
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Focus is moved to the trigger before the panel opens, because the menu's own hand-back
  // is suppressed (it would land behind the modal) and a selected item leaves focus on
  // nothing when it unmounts. The modal takes focus from there and returns it here on close.
  const openPanel = (next: Panel) => {
    triggerRef.current?.focus();
    setPanel(next);
  };

  useEffect(() => {
    let live = true;
    const read = () => {
      const at = ++epochRef.current;
      fetchDialerSettings()
        .then((r) => {
          if (!live || at !== epochRef.current) return;
          setQueued(countQueued(r.settings.event_transport));
          setStopped(Object.keys(r.tripped_pools).length);
          setFailed(false);
        })
        // A passenger on the console header — a failed read keeps the last known counts and
        // marks them unknown; opening an editor retries and surfaces the real error.
        .catch(() => {
          if (live && at === epochRef.current) setFailed(true);
        });
    };

    read();
    // The console stays open for hours and another session (or an API client) can flip any
    // of these, so re-read rather than trusting the mount-time answer.
    const timer = setInterval(read, 30_000);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, []);

  // Nothing to flag at neither end of the cutover: no stream queued is the old steady
  // state, every stream queued is the new one.
  const midCutover = queued != null && queued > 0 && queued < EVENT_STREAMS.length;
  // Deliberately ignores `failed`: losing contact with the count is not evidence the pools
  // came back, so the dot holds until a read says otherwise.
  const anyStopped = stopped != null && stopped > 0;

  // Stable: each modal's focus/Escape effect keys on it, and this component re-renders on
  // its own poll — a fresh identity would pull focus out of an open modal every tick.
  const closePanel = useCallback(() => setPanel(null), []);

  const applyQueued = (map: Parameters<typeof countQueued>[0]) => {
    epochRef.current++;
    setQueued(countQueued(map));
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button ref={triggerRef} variant="outline" size="sm">
            <Settings2 />
            Settings
            {/* A stopped pool outranks a cutover in progress: one is an incident, the other
                is a migration going to plan. */}
            {anyStopped ? (
              <>
                <span
                  aria-hidden
                  className="ml-1 inline-block h-2 w-2 rounded-full bg-destructive"
                />
                <span className="sr-only">
                  {stopped} {stopped === 1 ? 'pool' : 'pools'} stopped by dial admission on the node
                  that answered
                </span>
              </>
            ) : (
              midCutover && (
                <>
                  <span
                    aria-hidden
                    className="ml-1 inline-block h-2 w-2 rounded-full bg-amber-500"
                  />
                  <span className="sr-only">
                    {queued} of {EVENT_STREAMS.length} event streams on the queue
                  </span>
                </>
              )
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
          <DropdownMenuItem onSelect={() => openPanel('inbound')}>
            <PhoneIncoming />
            Inbound
            <RowStatus dot={inbound.dot} label={inbound.label} />
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => openPanel('manual')}>
            <PhoneOutgoing />
            Manual dialer
            <RowStatus dot={manual.dot} label={manual.label} />
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => openPanel('admission')}
            title="Pools this node's outcome breaker has stopped. Other gates defer dials without appearing here, and pools are spread across the fleet, so this is not the fleet total."
          >
            <ShieldAlert />
            Dial admission
            <RowStatus dot={stoppedDot(stopped, failed)} label={stoppedLabel(stopped, failed)} />
          </DropdownMenuItem>

          <DropdownMenuItem
            onSelect={() => openPanel('redial')}
            title="How long a lead rests after each ring, and how many times it may ever be rung."
          >
            <Repeat2 />
            Redial ladder
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem onSelect={() => openPanel('amd')}>
            <SlidersHorizontal />
            AMD settings
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => openPanel('gatekeeper')}
            title="The audio fork behind the voicemail classifier: how many answered calls are copied to it. It decides nothing yet."
          >
            <Voicemail />
            Voicemail classifier
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => openPanel('transport')}>
            <Route />
            Event transport
            <RowStatus label={queued == null ? '…' : queued === 0 ? 'direct' : `queue ${queued}`} />
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => openPanel('numbers')}>
            <Phone />
            Numbers settings
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {panel === 'inbound' && (
        <InboundCallModeModal
          onClose={closePanel}
          onSaved={(mode) => {
            inbound.apply(mode);
            closePanel();
          }}
        />
      )}
      {panel === 'manual' && (
        <ManualDialerModal
          onClose={closePanel}
          onSaved={(mode) => {
            manual.apply(mode);
            closePanel();
          }}
        />
      )}
      {panel === 'admission' && <DialAdmissionModal onClose={closePanel} />}
      {panel === 'redial' && <RedialLadderModal onClose={closePanel} />}
      {panel === 'amd' && <AmdSettingsModal onClose={closePanel} />}
      {panel === 'gatekeeper' && <GatekeeperSettingsModal onClose={closePanel} />}
      {panel === 'transport' && (
        <EventTransportModal
          onClose={closePanel}
          onLoaded={applyQueued}
          onSaved={(map) => {
            applyQueued(map);
            setPanel(null);
          }}
        />
      )}
      {panel === 'numbers' && <NumbersDashboard onClose={closePanel} />}
    </>
  );
}
